import React from "react";
import { DatePicker } from "antd";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setCompareStartAndEndDate,
  setIsAutoCompareDates,
  setDateRangeManuallyUpdated,
} from "../../redux/store/genericReportingSlice";
import dayjs from "dayjs";
import { useLazyGetDropdownsQuery } from "../../redux/api/dropdowns";
import { Skeleton, Select } from "antd";
import { setDefaultsFetchedForDropdown } from "../../redux/store/genericReportingSlice";
import { useDebounce } from "@uidotdev/usehooks";

const CompareDateRangeDatePicker = () => {
  const dispatch = useDispatch();

  const selectedClient = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['client_name']
  );
  const selectedStartDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['start_date']
  );
  const selectedEndDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['end_date']
  );
  const compareStartDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['compare_start_date']
  );
  const compareEndDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['compare_end_date']
  );
  const defaultsFetchedForDropdown = useSelector(
    (state) => state.genericReporting.defaultsFetchedForDropdown
  )
  const currentDropdownGroup = useSelector(
    (state) => state.genericReporting.currentDropdownGroup
  )
  const dateRangeManuallyUpdated = useSelector(
    (state) => state.genericReporting.dateRangeManuallyUpdated
  )

  // Debounce the start and end dates using the app's standard pattern
  const debouncedStartDate = useDebounce(selectedStartDate, 100);
  const debouncedEndDate = useDebounce(selectedEndDate, 100);
  const debouncedDateRangeManuallyUpdated = useDebounce(dateRangeManuallyUpdated, 500);

  const [triggerGetDropdowns, { data: dateRangeOptions, isLoading, isError, isFetching }] = useLazyGetDropdownsQuery();

  // Trigger the query when conditions are met
  useEffect(() => {
    if (debouncedStartDate && 
        debouncedEndDate && 
        defaultsFetchedForDropdown[currentDropdownGroup]?.['date_range'] && 
        !defaultsFetchedForDropdown[currentDropdownGroup]?.['compare_date_range']) {
      triggerGetDropdowns({
        data: {
          dropdown_name: 'compare_date_range',
          start_date: debouncedStartDate,
          end_date: debouncedEndDate,
          dropdown_group: currentDropdownGroup
        }
      });
    }
  }, [debouncedStartDate, debouncedEndDate, defaultsFetchedForDropdown, currentDropdownGroup, triggerGetDropdowns]);

  useEffect(() => {
    if (debouncedDateRangeManuallyUpdated && 
        debouncedStartDate && 
        debouncedEndDate && 
        defaultsFetchedForDropdown[currentDropdownGroup]?.['date_range']) {
      triggerGetDropdowns({
        data: {
          dropdown_name: 'compare_date_range',
          start_date: debouncedStartDate,
          end_date: debouncedEndDate,
          dropdown_group: currentDropdownGroup
        }
      });
      dispatch(setDateRangeManuallyUpdated(false));
    }
  }, [debouncedDateRangeManuallyUpdated, debouncedStartDate, debouncedEndDate, defaultsFetchedForDropdown, currentDropdownGroup, triggerGetDropdowns, dispatch]);

  useEffect(() => {
    console.log("defaultsFetchedForDropdown refresh");
    console.log('dateRangeOptions', dateRangeOptions);
    console.log('defaultsFetchedForDropdown', defaultsFetchedForDropdown);
    console.log('currentDropdownGroup', currentDropdownGroup);
    console.log('defaultsFetchedForDropdown[currentDropdownGroup]', defaultsFetchedForDropdown[currentDropdownGroup]);
    console.log('defaultsFetchedForDropdown[currentDropdownGroup]["compare_date_range"]', defaultsFetchedForDropdown[currentDropdownGroup]['compare_date_range']);

    if (dateRangeOptions?.default && dateRangeOptions.default[0] && dateRangeOptions.default[1]) {
      // Handle the default dates - they might already be formatted strings
      const startDate = dateRangeOptions.default[0];
      const endDate = dateRangeOptions.default[1];
      
      // If they're already strings, use them directly; otherwise format them
      const formattedStartDate = typeof startDate === 'string' ? startDate : dayjs(startDate).format("YYYY-MM-DD");
      const formattedEndDate = typeof endDate === 'string' ? endDate : dayjs(endDate).format("YYYY-MM-DD");
      
      dispatch(
        setCompareStartAndEndDate([
          formattedStartDate,
          formattedEndDate
        ])
      );
      dispatch(setDefaultsFetchedForDropdown({
        dropdownName: 'compare_date_range',
        value: true
      }));
    }
  }, [
    dateRangeOptions,
    defaultsFetchedForDropdown,
    currentDropdownGroup,
    isFetching
  ]);

  const manualCompareDates = (dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      return;
    }
    dispatch(setIsAutoCompareDates(false));
    dispatch(
      setCompareStartAndEndDate([
        dates[0].format("YYYY-MM-DD"),
        dates[1].format("YYYY-MM-DD"),
      ])
    );
  };

  // Helper function to get quarter start and end dates
  const getQuarterDates = (date) => {
    const month = date.month();
    const year = date.year();
    let quarterStartMonth;
    
    if (month < 3) {
      quarterStartMonth = 0; // Q1: Jan-Mar
    } else if (month < 6) {
      quarterStartMonth = 3; // Q2: Apr-Jun
    } else if (month < 9) {
      quarterStartMonth = 6; // Q3: Jul-Sep
    } else {
      quarterStartMonth = 9; // Q4: Oct-Dec
    }
    
    const quarterStart = dayjs().year(year).month(quarterStartMonth).startOf('month');
    const quarterEnd = quarterStart.add(2, 'month').endOf('month');
    
    return { quarterStart, quarterEnd };
  };

  // Helper function to get the previous quarter start date
  const getPreviousQuarterStart = (date) => {
    const threeMonthsAgo = date.subtract(3, 'months');
    const { quarterStart } = getQuarterDates(threeMonthsAgo);
    return quarterStart;
  };

  const presets = [
    {
      label: "Previous Year (YTD)",
      value: [dayjs().subtract(1, "year").startOf("year"), dayjs().subtract(1, "year").subtract(1, "day")],
    },
    {
      label: "Previous Month (MTD)",
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").subtract(1, "day"),
      ],
    },
    {
      label: "Previous Year's Month (MTD)",
      value: [
        dayjs().subtract(1, "year").startOf("month"),
        dayjs().subtract(1, "year").subtract(1, "day"),
      ],
    },
    {
      label: "WoW",
      value: (() => {
        if (!selectedStartDate || !selectedEndDate) return [null, null];
        const startDate = dayjs(selectedStartDate);
        
        // Find the previous week (entire week)
        const previousWeekStart = startDate.subtract(1, 'week').startOf('week');
        const previousWeekEnd = startDate.subtract(1, 'week').endOf('week');
        
        return [previousWeekStart, previousWeekEnd];
      })(),
    },
    {
      label: "MoM",
      value: (() => {
        if (!selectedStartDate || !selectedEndDate) return [null, null];
        const startDate = dayjs(selectedStartDate);
        
        // Find the previous month (entire month)
        const previousMonthStart = startDate.subtract(1, 'month').startOf('month');
        const previousMonthEnd = startDate.subtract(1, 'month').endOf('month');
        
        return [previousMonthStart, previousMonthEnd];
      })(),
    },
    {
      label: "QoQ",
      value: (() => {
        if (!selectedStartDate || !selectedEndDate) return [null, null];
        const startDate = dayjs(selectedStartDate);
        
        // Find the previous quarter (entire quarter)
        const previousQuarterStart = getPreviousQuarterStart(startDate);
        const { quarterEnd } = getQuarterDates(startDate.subtract(3, 'months'));
        
        return [previousQuarterStart, quarterEnd];
      })(),
    },
  ];

  // Safely convert dates to dayjs objects
  const getDateValue = (date) => {
    if (!date) return null;
    const dayjsDate = dayjs(date);
    return dayjsDate.isValid() ? dayjsDate : null;
  };

  if (isLoading || isFetching) {
    return <Select options={[]} value={[]} onChange={() => {}} loading={true} style={{ width: "16vw" }}></Select>
  }

  return (
    <DatePicker.RangePicker
      presets={presets}
      style={{
        width: "16vw",
      }}
      value={[getDateValue(compareStartDate), getDateValue(compareEndDate)]}
      onChange={manualCompareDates}
      maxDate={dateRangeOptions?.options ? getDateValue(dateRangeOptions.options[1]) : null}
      loading={isLoading || isFetching}
    />
  );
};

export default CompareDateRangeDatePicker;
